import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Wind,
  CloudRain,
  Flame,
  AlertOctagon,
  ShieldAlert,
  ArrowLeft,
  Activity,
  CheckCircle,
  AlertTriangle,
  Info,
  Waves,
} from 'lucide-react';
import { NWSAdapter } from '../services/nwsAdapter';
import { USGSAdapter } from '../services/usgsAdapter';
import { NormalizedAlert, AlertResponseMetadata } from '../types/alert';
import { AlertCard } from '../components/alerts/AlertCard';
import { AlertMap } from '../components/map/AlertMap';
import { FreshnessIndicator } from '../components/common/FreshnessIndicator';
import { DisclaimerBanner } from '../components/common/DisclaimerBanner';
import { AdSlot } from '../components/common/AdSlot';

interface HazardDefinition {
  title: string;
  tagline: string;
  description: string;
  filterKeywords: string[];
  isEarthquake?: boolean;
  colorClass: string;
  icon: React.ReactNode;
  safetyTips: string[];
}

const HAZARD_MAP: Record<string, HazardDefinition> = {
  tornado: {
    title: 'Active US Tornado Warnings & Watches Radar',
    tagline: 'Real-time detection of tornadic rotation, funnel clouds, and severe atmospheric rotation.',
    description: 'Tornado warnings indicate that a tornado is occurring or imminent based on Doppler radar velocity signatures or trained spotter reports.',
    filterKeywords: ['tornado'],
    colorClass: 'text-red-500',
    icon: <Wind className="w-6 h-6 text-red-500" />,
    safetyTips: [
      'Immediately seek shelter in a basement or lowest floor interior room.',
      'Stay away from windows, doors, and outside walls.',
      'Cover your head with a helmet, thick blankets, or cushions.',
      'Do not remain in a mobile home or vehicle.',
    ],
  },
  flood: {
    title: 'Active US Flash Flood & River Flood Radar',
    tagline: 'Rapid inundation warnings, small stream flooding, and dam breach alerts across the United States.',
    description: 'Flash floods develop rapidly, often within minutes to hours of excessive rainfall, dam failures, or sudden ice jam releases.',
    filterKeywords: ['flood', 'inundation', 'flash flood'],
    colorClass: 'text-blue-500',
    icon: <CloudRain className="w-6 h-6 text-blue-500" />,
    safetyTips: [
      'Turn Around, Don\'t Drown: never drive or walk through flooded roadways.',
      'Just 12 inches of moving water can carry away small vehicles.',
      'Move to higher ground if you reside in a low-lying or canyon area.',
      'Avoid camping or parking near drainage ditches during heavy rain.',
    ],
  },
  winter: {
    title: 'Active US Winter Storm & Blizzard Radar',
    tagline: 'Heavy snowfall, ice accumulation, freezing rain, and sub-zero wind chills.',
    description: 'Winter storms create treacherous travel conditions, heavy tree damage from ice loading, and potential regional power failures.',
    filterKeywords: ['winter', 'blizzard', 'snow', 'freeze', 'frost', 'ice storm', 'sleet'],
    colorClass: 'text-cyan-400',
    icon: <Wind className="w-6 h-6 text-cyan-400" />,
    safetyTips: [
      'Delay travel until highways and passes have been plowed and treated.',
      'Ensure vehicle emergency kit has extra blankets, water, and jumper cables.',
      'Never operate fuel-burning generators or charcoal grills inside the home.',
      'Protect outdoor pets, livestock, and uninsulated plumbing.',
    ],
  },
  severe: {
    title: 'Active Severe Thunderstorm & High Wind Radar',
    tagline: 'Damaging wind gusts of 58+ mph, destructive hail, and dangerous cloud-to-ground lightning.',
    description: 'Severe thunderstorms produce localized microbursts, structural roof damage, downed power lines, and hail capable of injuring individuals outdoors.',
    filterKeywords: ['thunderstorm', 'wind', 'gale', 'hail'],
    colorClass: 'text-orange-500',
    icon: <AlertOctagon className="w-6 h-6 text-orange-500" />,
    safetyTips: [
      'When Thunder Roars, Go Indoors: seek a sturdy enclosed building.',
      'Unplug sensitive electronics to avoid damage from power surges.',
      'Secure patio furniture, trash bins, and loose outdoor items.',
    ],
  },
  fire: {
    title: 'Active US Red Flag & Wildfire Weather Radar',
    tagline: 'Extreme fire behavior danger: high sustained winds, very low humidity, and dry fuels.',
    description: 'Red Flag Warnings are issued when weather conditions present an exceptionally high risk of rapid, uncontrollable wildfire ignition and spread.',
    filterKeywords: ['fire', 'red flag'],
    colorClass: 'text-amber-500',
    icon: <Flame className="w-6 h-6 text-amber-500" />,
    safetyTips: [
      'Strictly observe all outdoor burning and open camp fire bans.',
      'Do not discard smoking materials out vehicle windows.',
      'Avoid driving over tall dry grass where catalytic converters can spark fires.',
      'Keep your emergency "Go-Bag" and vehicle fuel tank full.',
    ],
  },
  earthquake: {
    title: 'Active US & Global Seismic Radar (USGS)',
    tagline: 'Real-time earthquake monitoring with magnitude, depth, and tsunami risk flags.',
    description: 'Monitored by the USGS National Earthquake Information Center across the United States and globally for events exceeding Magnitude 2.5.',
    filterKeywords: [],
    isEarthquake: true,
    colorClass: 'text-purple-400',
    icon: <Activity className="w-6 h-6 text-purple-400" />,
    safetyTips: [
      'Drop, Cover, and Hold On: protect head and torso under a heavy desk or table.',
      'Stay indoors until shaking stops; do not run outside during shaking.',
      'If in bed, stay there and protect your head with a pillow.',
      'Expect aftershocks following any moderate or severe earthquake.',
    ],
  },
  tropical: {
    title: 'Active US Hurricane & Tropical Storm Radar',
    tagline: 'Tracking tropical cyclones, storm surge inundation, and hurricane-force wind threats.',
    description: 'Official National Hurricane Center and Weather Forecast Office advisories for hurricanes, tropical storms, and destructive coastal surges.',
    filterKeywords: ['hurricane', 'tropical storm', 'typhoon', 'storm surge'],
    colorClass: 'text-red-500',
    icon: <Wind className="w-6 h-6 text-red-500" />,
    safetyTips: [
      'Follow all mandatory local evacuation orders immediately.',
      'Board up windows and secure all outdoor objects.',
      'Keep at least 7 days of bottled drinking water, non-perishable food, and medications.',
      'Never run portable fuel generators indoors or near open windows.',
    ],
  },
  marine: {
    title: 'Active US Coastal & Marine Hazard Radar',
    tagline: 'Monitoring gale-force winds, hazardous seas, high surf, and life-threatening rip currents.',
    description: 'Official National Weather Service coastal advisories protecting recreational boaters, commercial fishing vessels, and coastal communities.',
    filterKeywords: ['gale', 'surf', 'rip current', 'small craft', 'hazardous seas', 'marine'],
    colorClass: 'text-teal-400',
    icon: <Waves className="w-6 h-6 text-teal-400" />,
    safetyTips: [
      'Inexperienced swimmers should stay completely out of the surf during Rip Current advisories.',
      'If caught in a rip current, remain calm and swim parallel to the shoreline until free.',
      'Recreational vessels should remain in harbor during Gale and Small Craft warnings.',
      'Never turn your back on breaking ocean surf or stand on slick coastal jetty rocks.',
    ],
  },
};

export const HazardPage: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const [alerts, setAlerts] = useState<NormalizedAlert[]>([]);
  const [metadata, setMetadata] = useState<AlertResponseMetadata | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const hazardKey = type ? type.toLowerCase() : 'tornado';
  const def = HAZARD_MAP[hazardKey] || HAZARD_MAP.tornado;

  const loadData = async (force: boolean = false) => {
    try {
      if (force) setIsRefreshing(true);
      else setLoading(true);
      setError(null);

      if (def.isEarthquake) {
        const res = await USGSAdapter.fetchEarthquakes({ forceRefresh: force });
        setAlerts(res.alerts);
        setMetadata(res.metadata);
      } else {
        const res = await NWSAdapter.fetchActiveAlerts({ forceRefresh: force });
        setAlerts(res.alerts);
        setMetadata(res.metadata);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to retrieve hazard alert feed');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadData(false);
  }, [hazardKey]);

  // Filter alerts by hazard keywords
  const filteredAlerts = useMemo(() => {
    if (def.isEarthquake) return alerts;

    return alerts.filter((a) => {
      const text = `${a.event} ${a.headline} ${a.description}`.toLowerCase();
      return def.filterKeywords.some((kw) => text.includes(kw));
    });
  }, [alerts, def]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      {/* Back link */}
      <div>
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline font-bold">
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to National Radar Overview</span>
        </Link>
      </div>

      {/* Hazard Header Banner */}
      <section className="bg-gradient-to-r from-navy-950 via-navy-900 to-navy-950 text-white rounded-3xl p-6 sm:p-10 border border-slate-800 shadow-xl space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-slate-900 border border-slate-700 flex items-center justify-center shadow-md">
            {def.icon}
          </div>
          <div>
            <div className="text-xs font-mono font-semibold uppercase text-cyan-400 tracking-wider">
              OFFICIAL HAZARD INTELLIGENCE RADAR
            </div>
            <h1 className="text-2xl sm:text-4xl font-black text-white tracking-tight">
              {def.title}
            </h1>
          </div>
        </div>

        <p className="text-sm sm:text-base text-slate-300 leading-relaxed max-w-3xl font-normal">
          {def.description}
        </p>

        {/* Quick Nav Chips */}
        <div className="pt-2 flex flex-wrap items-center gap-2 text-xs font-mono">
          <span className="text-slate-400 font-bold uppercase text-[11px]">All Hazard Radars:</span>
          {Object.entries(HAZARD_MAP).map(([k, v]) => (
            <Link
              key={k}
              to={`/hazards/${k}`}
              className={`px-3 py-1 rounded-lg border transition-all ${
                k === hazardKey
                  ? 'bg-blue-600 text-white font-bold border-blue-500 shadow-xs'
                  : 'bg-slate-800/80 text-slate-300 border-slate-700/80 hover:bg-slate-700'
              }`}
            >
              {k.toUpperCase()}
            </Link>
          ))}
        </div>
      </section>

      {/* Freshness Bar */}
      <FreshnessIndicator
        metadata={metadata}
        onRefresh={() => loadData(true)}
        isRefreshing={isRefreshing}
      />

      {/* Safety Notice */}
      <DisclaimerBanner />

      {/* Specific Hazard Map */}
      <section aria-labelledby="hazard-map-heading" className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 id="hazard-map-heading" className="text-lg font-black text-navy-950 flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-cyan-500 animate-pulse" />
            <span>Active {def.title} Geospatial Plot</span>
          </h2>
          <span className="text-xs font-mono text-slate-500 font-bold">
            {filteredAlerts.length} ACTIVE EVENTS
          </span>
        </div>

        <AlertMap alerts={filteredAlerts} height="460px" />
      </section>

      {/* Safety & Action Tips Box */}
      <section className="bg-slate-900 text-slate-200 rounded-2xl p-6 border border-slate-800 space-y-3">
        <h3 className="text-base font-bold text-white flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-amber-400" />
          <span>Authoritative Preparedness Actions: {def.title}</span>
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          {def.safetyTips.map((tip, idx) => (
            <div key={idx} className="flex items-start gap-2 bg-slate-800/60 p-3 rounded-xl border border-slate-700">
              <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              <span className="leading-relaxed">{tip}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Active Alerts Feed */}
      <section aria-labelledby="hazard-alerts-heading" className="space-y-4">
        <h2 id="hazard-alerts-heading" className="text-xl font-black text-navy-950">
          Currently Active Bulletins ({filteredAlerts.length})
        </h2>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-52 bg-slate-100 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="p-8 bg-red-50 border border-red-200 rounded-2xl text-center space-y-2">
            <AlertTriangle className="w-8 h-8 text-red-600 mx-auto" />
            <p className="text-xs sm:text-sm text-red-800">{error}</p>
          </div>
        ) : filteredAlerts.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center space-y-2 max-w-lg mx-auto">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto" />
            <h3 className="font-bold text-navy-950 text-base">
              No Active {def.title} Reported
            </h3>
            <p className="text-xs text-slate-600">
              Official feeds report zero active warnings matching this hazard category at this time.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredAlerts.map((alert) => (
              <AlertCard key={alert.id} alert={alert} />
            ))}
          </div>
        )}
      </section>

      {/* Reserved Ad Slot */}
      <AdSlot slotId="hazard-page-bottom" format="horizontal" />
    </div>
  );
};

